import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { taskAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Timer, Clock, User } from 'lucide-react';

export default function TimeTrackingPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    taskAPI.timeEntries(currentWorkspace.id, null, 30)
      .then(res => setEntries(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentWorkspace?.id]);

  // Group by user
  const byUser = {};
  entries.forEach(e => {
    const uid = e.user_id || 'unknown';
    if (!byUser[uid]) byUser[uid] = { name: e.user_name || 'Unknown', hours: 0, entries: [] };
    byUser[uid].hours += e.hours || 0;
    byUser[uid].entries.push(e);
  });

  // Group by date
  const byDate = {};
  entries.forEach(e => {
    const d = e.date || 'unknown';
    if (!byDate[d]) byDate[d] = { hours: 0, entries: [] };
    byDate[d].hours += e.hours || 0;
    byDate[d].entries.push(e);
  });

  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="time-tracking-page">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">Time Tracking</h1>
        <p className="text-sm text-muted-foreground mt-1">Last 30 days &middot; {totalHours.toFixed(1)} total hours</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <Timer className="h-5 w-5 text-cyan-400 mb-2" />
            <p className="text-2xl font-display font-semibold text-cyan-400" data-font="display">{totalHours.toFixed(1)}h</p>
            <p className="text-xs text-muted-foreground">Total Hours (30d)</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <User className="h-5 w-5 text-primary mb-2" />
            <p className="text-2xl font-display font-semibold" data-font="display">{Object.keys(byUser).length}</p>
            <p className="text-xs text-muted-foreground">Team Members</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <Clock className="h-5 w-5 text-amber-400 mb-2" />
            <p className="text-2xl font-display font-semibold" data-font="display">{entries.length}</p>
            <p className="text-xs text-muted-foreground">Time Entries</p>
          </CardContent>
        </Card>
      </div>

      {/* By Team Member */}
      <Card className="bg-card/60 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Hours by Team Member</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(byUser).sort((a, b) => b[1].hours - a[1].hours).map(([uid, data]) => (
            <div key={uid} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
              <span className="text-sm font-medium">{data.name}</span>
              <span className="text-sm font-display font-semibold text-cyan-400" data-font="display">{data.hours.toFixed(1)}h</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <Card className="bg-card/60 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Recent Entries</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {entries.slice(0, 20).map(e => (
            <div key={e.id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/20 text-xs">
              <span className="font-mono text-muted-foreground w-20 shrink-0">{e.date}</span>
              <span className="font-medium text-cyan-400 w-10 shrink-0">{e.hours}h</span>
              <span className="font-medium truncate flex-1">{e.task_title}</span>
              <span className="text-muted-foreground shrink-0">{e.user_name}</span>
              {e.note && <span className="text-muted-foreground/60 truncate max-w-[120px]">{e.note}</span>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
